import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { Notification, JobStatus, JobStatusResponse } from 'app/layout/common/notifications/notifications.types';
import { map, switchMap, take } from 'rxjs/operators';
import { RelatedToType } from '@shared/enums';
import { DIRECT_DOWNLOAD_JOBS } from '@core/services/direct-download-jobs';
import { WINDOW } from 'app/providers/window.provider';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private _notifications: BehaviorSubject<Notification[] | null>;
  private _refreshTrigger = new Subject<void>();
  private readonly window = inject(WINDOW);

  readonly refreshTrigger$ = this._refreshTrigger.asObservable();

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(private _httpClient: HttpClient) {
    // Set the private defaults
    this._notifications = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for notifications
   */
  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Store notifications on the service
   *
   * @param notifications
   */
  store(notifications: Notification[]): Observable<Notification[]> {
    // Load the notifications
    this._notifications.next(notifications);

    // Return the notifications
    return this.notifications$;
  }

  /**
   * Create a notification
   *
   * @param notification
   */
  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient.put<Notification>('api/common/notifications', { notification }).pipe(
          map((newNotification) => {
            // Update the notifications with the new notification
            this._notifications.next([...notifications, newNotification]);

            // Return the new notification from observable
            return newNotification;
          }),
        ),
      ),
    );
  }

  /**
   * Update the notification
   *
   * @param id
   * @param notification
   */
  update(id: number, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .patch<Notification>('api/common/notifications', {
            id,
            notification,
          })
          .pipe(
            map((updatedNotification: Notification) => {
              // Find the index of the updated notification
              const index = notifications.findIndex((item) => item.id === id);

              // Update the notification
              notifications[index] = updatedNotification;

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated notification
              return updatedNotification;
            }),
          ),
      ),
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient.get<boolean>('api/common/notifications/mark-all-as-read').pipe(
          map((isUpdated: boolean) => {
            // Go through all notifications and set them as read
            notifications.forEach((notification, index) => {
              notifications[index].is_read = true;
            });

            // Update the notifications
            this._notifications.next(notifications);

            // Return the updated status
            return isUpdated;
          }),
        ),
      ),
    );
  }

  getNotifications(): Observable<Notification[]> {
    return this._httpClient
      .get<{ system_notifications: Notification[] }>('user/notifications')
      .pipe(
        map((res) =>
          res.system_notifications.map((notification) => this.withDealLink({ ...notification, source: 'system' })),
        ),
      );
  }

  markNotificationsAsRead(notifications: Notification[]): Observable<void> {
    this.markAsReadLocally(notifications);
    return this._httpClient.post<void>(`user/read_notifications`, {});
  }

  getInProgressJobs(userId: number): Observable<JobStatus[]> {
    const params = new HttpParams()
      .append('status[]', 'enqueued')
      .append('status[]', 'in_progress')
      .append('job_purpose[]', 'object_import')
      .append('job_purpose[]', 'portfolio_creation')
      .append('job_purpose[]', 'object_export');

    return this._httpClient
      .get<JobStatusResponse>('job_statuses', { params })
      .pipe(
        map((res) =>
          res.job_statuses.filter((job) =>
            job.job_purpose === 'object_export'
              ? this.isUserDirectDownloadJob(job, userId)
              : job.status === 'in_progress',
          ),
        ),
      );
  }

  getDirectDownloadNotifications(userId: number): Observable<Notification[]> {
    const params = new HttpParams()
      .append('status[]', 'complete')
      .append('job_purpose[]', 'object_export')
      .append('per_page', '50');

    return this._httpClient.get<JobStatusResponse>('job_statuses', { params }).pipe(
      map((res) => {
        const readIds = this.getLocallyReadIds('job_status');

        return res.job_statuses
          .filter((job) => this.isUserDirectDownloadJob(job, userId) && !!job.latest_document_url)
          .map((job) => this.toNotification(job, readIds.includes(job.id)));
      }),
    );
  }

  markNotificationAsRead(notification: Notification): Observable<void> {
    if (notification.source !== 'system') {
      this.markAsReadLocally([notification]);
      return of(undefined);
    }

    return this._httpClient.post<void>('user/read_notifications', {
      notification_ids: [notification.id],
    });
  }

  triggerRefresh(): void {
    this._refreshTrigger.next();
  }

  private markAsReadLocally(notifications: Notification[]): void {
    notifications
      .filter(({ source }) => source !== 'system')
      .forEach(({ source, id }) => {
        const readIds = new Set([...this.getLocallyReadIds(source), id]);
        this.window.localStorage.setItem(`read-${source}-notifications`, JSON.stringify([...readIds]));
      });
  }

  private getLocallyReadIds(source: string): number[] {
    try {
      const ids = JSON.parse(this.window.localStorage.getItem(`read-${source}-notifications`) ?? '[]');
      return Array.isArray(ids) ? ids : [];
    } catch {
      return [];
    }
  }

  private isUserDirectDownloadJob(job: JobStatus, userId: number): boolean {
    return job.user_id === userId && !!DIRECT_DOWNLOAD_JOBS[job.job_class];
  }

  private toNotification(job: JobStatus, isRead: boolean): Notification {
    return {
      id: job.id,
      receiver_id: job.user_id,
      receiver_type: 'User',
      related_id: job.id,
      related_type: 'JobStatus',
      subject: job.label,
      body: DIRECT_DOWNLOAD_JOBS[job.job_class],
      is_read: isRead,
      extra: {},
      created_at: job.completed_at,
      link: job.latest_document_url,
      source: 'job_status',
    };
  }

  private withDealLink(notification: Notification): Notification {
    const pipelineId = notification.extra?.pipeline_id;

    if (notification.link || notification.related_type !== RelatedToType.Project || !pipelineId) {
      return notification;
    }

    return { ...notification, link: `/projects/${pipelineId}/${notification.related_id}`, useRouter: true };
  }
}
